import { cn } from "../utils/cn.js";

export interface OwlMarkProps {
  /** Outer pixel size of the round badge. Default 40. */
  size?: number;
  /** Background color of the disc. Use `bg-mustard` for the Companion glow,
   *  `bg-teal` everywhere else. */
  bgClassName?: string;
  /** When true, adds the mustard halo glow — Companion mode only. */
  glow?: boolean;
  /** Override the image source. Default `/wot-logo-512.png` from /public. */
  src?: string;
  className?: string;
}

/**
 * OwlMark — round-cropped owl avatar.
 *
 * Used as the AI speaker avatar (chat bubbles, daily-insight cards)
 * and as the brand mark in the app sidebar. The image is sized
 * slightly larger than the badge (×1.06) and nudged down (+2%) so the
 * "Wise Owl Talks" wordmark in the source JPG crops out of frame and
 * only the owl's head shows.
 *
 * Per the brand bundle: the full owl+mic illustration is reserved for
 * marketing pages. Inside the app shell, use this round-cropped form.
 */
export const OwlMark = ({
  size = 40,
  bgClassName = "bg-teal",
  glow = false,
  src = "/wot-logo-512.png",
  className,
}: OwlMarkProps) => (
  <span
    className={cn(
      "relative inline-grid shrink-0 place-items-center overflow-hidden rounded-pill",
      bgClassName,
      glow ? "shadow-glow" : "shadow-1",
      className,
    )}
    style={{ width: size, height: size }}
  >
    <img
      src={src}
      alt=""
      aria-hidden="true"
      draggable={false}
      style={{
        width: size * 1.06,
        height: size * 1.06,
        objectFit: "cover",
        transform: "translateY(2%)",
      }}
    />
  </span>
);
